import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { User } from 'models/user';
import { OrderInputType, PatientFormData } from 'models/order-input/shared';
import PatientForm from 'components/order-flow/shared/patient-form.component';
import OrderDentist from 'components/order-flow/shared/order-dentist.component';
import OrderParticipants from 'components/order-flow/shared/order-participants.component';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import { LinkedClinic, LinkedUser } from 'models/order';
import { useMutation } from '@apollo/client';
import { saveOrderDraftTemplate } from 'apollo/templates/order';
import { actionOrderPatientDraft, hasRequiredPatientNames } from 'components/order-flow/shared/order-input-helper';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';

type PatientStepProps = {
    onNextClick: () => void;
};
const PatientStep: FC<PatientStepProps> = ({ onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders', 'patient']);
    const { order, setOrder } = useContext(ActionOrderContext);
    const [isPatientFormValid, setIsPatientFormValid] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentUser = currentUserVar();

    const [saveOrderDraft, { loading }] = useMutation(saveOrderDraftTemplate, {
        onCompleted: ({ saveOrderDraft }) => {
            setOrder((prev) => ({
                ...prev,
                ...saveOrderDraft,
            }));
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_SAVED') });
            onNextClick();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const isPatientComplete = isPatientFormValid && hasRequiredPatientNames(order.patient);

    const handlePatientChange = (formData: PatientFormData, isFormValid: boolean) => {
        setIsPatientFormValid(isFormValid);
        setOrder((prev) => ({
            ...prev,
            patient: {
                ...prev.patient,
                ...formData,
            },
        }));
    };

    const handleDentistChanged = (dentist: Partial<User>) => {
        setOrder((prev) => ({
            ...prev,
            dentist,
        }));
    };

    const handleParticipantsChange = (
        linkedDentists: LinkedUser[],
        linkedClinics: LinkedClinic[],
        customParticipantEmails: string[],
    ) => {
        setOrder((prev) => ({
            ...prev,
            participants: {
                linkedDentists,
                linkedClinics,
                customParticipantEmails,
            },
        }));
    };

    return (
        <Fragment>
            {order ? (
                <Box>
                    <Box mb={2}>
                        {order.inputType === OrderInputType.NEW ? (
                            <Typography variant="h1">{translate('orders:NEW_ORDER')}</Typography>
                        ) : (
                            <Typography variant="h1">{translate('orders:UPDATE_ORDER')}</Typography>
                        )}
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    <Box mb={4}>
                        <Box mb={1}>
                            <Typography variant="h2">{translate('orders:PATIENT_INFORMATION')}</Typography>
                        </Box>
                        <PatientForm
                            isEdit={order.inputType !== OrderInputType.NEW}
                            patient={order.patient}
                            onPatientChange={handlePatientChange}
                        />
                    </Box>
                    <Box mb={4}>
                        <Box mb={1}>
                            <Typography variant="h2">{translate('orders:DENTIST_AND_PARTICIPANTS')}</Typography>
                        </Box>
                        <Grid container spacing={3} display="flex" alignItems="stretch">
                            <Grid item xs={12} sm={4}>
                                <OrderDentist currentDentist={order.dentist} onDentistChanged={handleDentistChanged} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <OrderParticipants
                                    participants={order.participants}
                                    onParticipantsChanged={handleParticipantsChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box display="flex" justifyContent="center">
                        {((order._id && !order.isDraft) || permissions.isLabTechnician(currentUser.type)) && (
                            <Button
                                disabled={!isPatientComplete || !order.dentist}
                                type="button"
                                onClick={onNextClick}
                                variant="contained"
                            >
                                {translate('common:NEXT_STEP')}
                            </Button>
                        )}
                        {(!order._id || order.isDraft) && !permissions.isLabTechnician(currentUser.type) && (
                            <LoadingButton
                                loading={loading}
                                variant="contained"
                                disabled={!isPatientComplete || !order.dentist || loading}
                                type="button"
                                onClick={() =>
                                    saveOrderDraft({
                                        variables: { orderId: order._id, order: actionOrderPatientDraft(order) },
                                    })
                                }
                                data-test="patient-step.draft-submit"
                            >
                                {translate('common:SAVE_AND_NEXT')}
                            </LoadingButton>
                        )}
                    </Box>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default PatientStep;
